import React, { createContext, ReactElement, ReactNode, useCallback, useContext, useMemo, useRef } from 'react';
import Modal61 from '@Common/Modal/Modal61';
import type { Modal61Ref } from '@Types/common';
import './ScoreInfoModal.scss';

export const SCORE_INFO_HELP_URL = 'https://ehsolution.zendesk.com/hc/en-us/articles/52750077103515-Understanding-Health-Score';

const ScoreInfoModalContext = createContext<(() => void) | null>(null);

export const ScoreInfoModalProvider = ({ children }: { children: ReactNode }): ReactElement => {
    const modalRef = useRef<Modal61Ref | null>(null);
    const open = useCallback(() => modalRef.current?.show(), []);

    return (
        <ScoreInfoModalContext.Provider value={open}>
            {children}
            <Modal61
                ref={modalRef}
                title='About Health & Risk Scores'
                size='md'
                isCentered
                primaryButtonText='Close'
                customClassName='score-info-modal'
                body={
                    <div className='score-info-modal__body'>
                        <p>
                            Health Score shows what is happening right now, such as overdue milestones, overdue tasks, or
                            unassigned work. A lower Health Score means something needs attention today.
                        </p>
                        <p>
                            Risk Score shows problems that may happen soon if nothing changes, such as work falling behind
                            pace or ownership gaps. A higher Risk Score is an early warning, before it becomes a Health
                            problem.
                        </p>
                        <p>Scores update up to 3 times a day and may not reflect changes made in the last few hours.</p>
                        <a href={SCORE_INFO_HELP_URL} target='_blank' rel='noopener noreferrer'>
                            Learn more about Health & Risk Scores
                        </a>
                    </div>
                }
            />
        </ScoreInfoModalContext.Provider>
    );
};

type ScoreInfoTriggerProps = Pick<
    React.HTMLAttributes<HTMLElement>,
    'className' | 'role' | 'tabIndex' | 'onClick' | 'onKeyDown' | 'aria-haspopup' | 'aria-label'
>;

interface ScoreInfoTriggerOptions {
    label: string;
    score: number | null;
    unavailableReason?: string | null;
}

export const useScoreInfoTrigger = ({ label, score, unavailableReason }: ScoreInfoTriggerOptions): ScoreInfoTriggerProps => {
    const open = useContext(ScoreInfoModalContext);

    return useMemo(() => {
        if (!open) return {};
        const value = score !== null ? score.toFixed(1) : `unavailable${unavailableReason ? ` (${unavailableReason})` : ''}`;
        return {
            'aria-label': `${label} score ${value}. About Health & Risk Scores`,
            className: 'score-info-trigger',
            role: 'button',
            tabIndex: 0,
            'aria-haspopup': 'dialog',
            onClick: (e: React.MouseEvent) => {
                e.stopPropagation();
                open();
            },
            onKeyDown: (e: React.KeyboardEvent) => {
                if (e.key !== 'Enter' && e.key !== ' ') return;
                e.preventDefault();
                e.stopPropagation();
                if (!e.repeat) open();
            },
        };
    }, [open, label, score, unavailableReason]);
};
